import {
	StakingEnrollmentsScreen_GetStakingEnrollments_stakingEnrollments_nodes as StakingEnrollment,
	StakingEnrollmentStatus,
} from '@app/codegen';
import { StakingEnrollmentsLocators } from '@app/e2e/screens/StakingEnrollments';
import {
	CustomDataTable,
	DangerAssetSVG,
	ITrustAction,
	ITrustColumn,
} from '@app/shared/components';
import { AppAlert, dateFormat, humanize, useAppDevice, useAppState } from '@itrustcapital/ui';
import { format } from 'date-fns';
import React from 'react';
import Toast from 'react-native-toast-message';

import { useCancelEnrollmentMutation } from '../StakingEnrollmentsGraphql';
import { useStakingEnrollmentsScreenContext } from '../StakingEnrollmentsScreenProvider';

type GroupHeaderRow = { isGroupHeader: boolean; createdAt: string };

type RowData = StakingEnrollment | GroupHeaderRow;

export function StakingEnrollmentsTable() {
	const width = useAppDevice().width;
	const context = useStakingEnrollmentsScreenContext();

	const columnWidths = Math.max(width / 5, 200);
	const cancelVisible = useAppState(false);

	const selectedRow = useAppState<StakingEnrollment | null>(null);
	const [cancelEnrollment, cancelEnrollmentMutation] = useCancelEnrollmentMutation();

	const enrollments = context.stakingEnrollmentsQuery?.data?.stakingEnrollments?.nodes ?? [];

	const groupedData = enrollments.reduce<Record<string, StakingEnrollment[]>>((acc, item) => {
		const date = format(new Date(item?.createdAt), 'yyyy-MM-dd');

		if (!acc[date]) acc[date] = [];

		if (item) {
			acc[date].push(item);
		}

		return acc;
	}, {} as Record<string, StakingEnrollment[]>);

	const flattenedData = Object.entries(groupedData).flatMap(([date, items]) => [
		{ isGroupHeader: true, createdAt: date },
		...items,
	]);

	const renderDescription = (row: StakingEnrollment) => {
		switch (row.status) {
			case StakingEnrollmentStatus.ENROLLED:
				return `Enrolled at ${dateFormat(row?.createdAt)}`;
			case StakingEnrollmentStatus.STAKED:
				return `Staked at ${dateFormat(row?.stakedAt)}`;
			case StakingEnrollmentStatus.UNSTAKED:
				return `Unstaked at ${dateFormat(row?.unstakedAt)}`;
			case StakingEnrollmentStatus.COMPLETED:
				return `Completed at ${dateFormat(row?.completedAt)}`;
		}
	};

	const onCancelEnrollment = async () => {
		try {
			await cancelEnrollment({
				variables: {
					stakingEnrollmentId: selectedRow.get?.id || 0,
				},
			});

			Toast.show({
				text2: 'Enrollment has been canceled',
				type: 'success',
			});

			cancelVisible.set(false);
			context.stakingEnrollmentsQuery.refetch();
		} catch (error) {
			Toast.show({
				text2: 'Enrollment failed to cancel',
				type: 'error',
			});
		}
	};

	const stakingEnrollmentColumns: ITrustColumn<RowData>[] = [
		{
			key: 'createdAt',
			name: 'Created At',
			maxWidth: 150,
			sortable: false,
			center: false,
			selector: (row) => ('isGroupHeader' in row ? dateFormat(row?.createdAt) : ''),
		},
		{
			key: 'accountNumber',
			name: 'Account Number',
			maxWidth: 125,
			sortable: false,
			center: false,
			cell: (row) => ('isGroupHeader' in row ? '' : row.account?.accountNumber),
		},
		{
			key: 'id',
			name: 'Enrollment ID',
			maxWidth: 150,
			sortable: false,
			center: false,
			cell: (row) => ('isGroupHeader' in row ? '' : row.id),
		},
		{
			key: 'ticker',
			name: 'Enrollment',
			maxWidth: 300,
			sortable: false,
			center: false,
			cell: (row) =>
				'isGroupHeader' in row ? (
					''
				) : (
					<p>
						{row.currency?.ticker} {renderDescription(row)}
					</p>
				),
		},
		{
			key: 'principle',
			name: 'Principle',
			maxWidth: columnWidths,
			sortable: false,
			center: false,
			cell: (row) =>
				'isGroupHeader' in row ? (
					''
				) : (
					<p>
						{row.principle} {row.currency?.ticker}
					</p>
				),
		},
		{
			key: 'reward',
			name: 'Reward',
			maxWidth: columnWidths,
			sortable: false,
			center: false,
			cell: (row) =>
				'isGroupHeader' in row ? (
					''
				) : (
					<p>
						{row?.reward ? row.reward : 0} {row.currency?.ticker}
					</p>
				),
		},
		{
			key: 'status',
			name: 'Status',
			maxWidth: 150,
			sortable: false,
			center: false,
			cell: (row) => ('isGroupHeader' in row ? '' : humanize(row?.status)),
		},
	];

	const actions: ITrustAction<RowData>[] = [
		{
			name: 'Cancel',
			isHidden: (row) => 'isGroupHeader' in row,
			handler: (row) => {
				if ('isGroupHeader' in row) return;

				selectedRow.set(row);
				cancelVisible.set(true);
			},
			isDisabled: (row) => {
				if ('isGroupHeader' in row) return true;

				return row.status !== StakingEnrollmentStatus.ENROLLED;
			},
			iconLib: 'matc',
			iconName: 'cancel',
		},
	];

	return (
		<>
			<AppAlert
				actions={[
					{
						testID: StakingEnrollmentsLocators.cancelEnrollmentModalCancelButton,

						title: 'Cancel',
						status: 'primary',
						appearance: 'outline',
						onPress: () => {
							selectedRow.set(null);
							cancelVisible.set(false);
						},
					},
					{
						testID: StakingEnrollmentsLocators.cancelEnrollmentModalConfirmButton,
						title: 'Confirm',
						status: 'danger',
						onPress: onCancelEnrollment,
						loading: cancelEnrollmentMutation.loading,
					},
				]}
				message="Do you really want to cancel this enrollment?"
				title="Are you sure?"
				visible={cancelVisible.get}
				xmlIcon={DangerAssetSVG}
			/>
			<CustomDataTable
				columns={stakingEnrollmentColumns}
				data={flattenedData}
				progressPending={context.stakingEnrollmentsQuery.loading}
				refetch={(variables) =>
					context.stakingEnrollmentsQuery?.refetch?.({
						...variables,
						where: context.tableState.where.get,
					})
				}
				rowActions={actions}
				{...context.tableState.props}
			/>
		</>
	);
}
